import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, Eye, EyeOff, Loader2, X, Play, FileText } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { adminApi, mediaRaw } from "./adminApi";
import { StatusBadge, Field, LinesEditor, MediaPicker } from "./components";
import { DynIcon, ICON_NAMES } from "@/lib/icons";

const EMPTY = {
  name: "", subtitle: "", short_description: "", description: "", icon: "Shield",
  hero_image_url: "", thumbnail_url: "", benefits: [], capabilities: [], asset_types: [],
  industries: [], images: [], videos: [], pdfs: [], email_enabled: true, order: 0, status: "draft",
};

export default function SolutionsAdmin() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["admin-solutions"], queryFn: adminApi.listSolutions });
  const [editing, setEditing] = useState(null);

  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-solutions"] });
  const list = data || [];

  const move = async (idx, dir) => {
    const arr = [...list];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    await adminApi.reorderSolutions(arr.map((s) => s.id));
    refresh();
  };

  const toggleStatus = async (s) => {
    await adminApi.solutionStatus(s.id, s.status === "published" ? "draft" : "published");
    toast.success(s.status === "published" ? "Unpublished" : "Published");
    refresh();
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.name}"? This cannot be undone.`)) return;
    await adminApi.deleteSolution(s.id);
    toast.success("Deleted");
    refresh();
  };

  return (
    <div data-testid="admin-solutions-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-head text-3xl font-extrabold text-white">Solutions</h1>
          <p className="mt-1 text-slate-400">Create, edit, reorder and publish kiosk solutions.</p>
        </div>
        <Button data-testid="solution-add-btn" onClick={() => setEditing(EMPTY)} className="cyan-grad text-[#041021] font-bold">
          <Plus className="h-4 w-4" /> New Solution
        </Button>
      </div>

      {isLoading ? <Loader2 className="mt-10 h-8 w-8 animate-spin text-cyan-400" /> : (
        <div className="mt-6 space-y-3">
          {list.length === 0 && <p className="rounded-2xl glass p-8 text-center text-slate-500">No solutions yet. Create your first one.</p>}
          {list.map((s, i) => (
            <div key={s.id} data-testid={`solution-row-${i}`} className="flex items-center gap-4 rounded-2xl glass p-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl cyan-grad text-[#041021]"><DynIcon name={s.icon} className="h-6 w-6" /></div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2"><h3 className="truncate font-head font-bold text-white">{s.name}</h3><StatusBadge status={s.status} /></div>
                <p className="truncate text-sm text-slate-400">{s.short_description}</p>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <button onClick={() => move(i, -1)} data-testid={`solution-up-${i}`} className="rounded-lg p-2 text-slate-400 hover:text-cyan-300"><ArrowUp className="h-4 w-4" /></button>
                <button onClick={() => move(i, 1)} data-testid={`solution-down-${i}`} className="rounded-lg p-2 text-slate-400 hover:text-cyan-300"><ArrowDown className="h-4 w-4" /></button>
                <button onClick={() => toggleStatus(s)} data-testid={`solution-publish-${i}`} className="rounded-lg p-2 text-slate-400 hover:text-emerald-300">{s.status === "published" ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
                <button onClick={() => setEditing(s)} data-testid={`solution-edit-${i}`} className="rounded-lg p-2 text-slate-400 hover:text-cyan-300"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => remove(s)} data-testid={`solution-delete-${i}`} className="rounded-lg p-2 text-slate-400 hover:text-rose-300"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && <SolutionEditor initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refresh(); }} />}
    </div>
  );
}

function SolutionEditor({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [picker, setPicker] = useState(null); // {type}
  const [vTitle, setVTitle] = useState(""); const [vUrl, setVUrl] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (publish) => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const body = { ...form, status: publish ? "published" : form.status };
      if (initial.id) await adminApi.updateSolution(initial.id, body);
      else await adminApi.createSolution(body);
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const onPick = (m) => {
    if (picker === "hero") setForm((f) => ({ ...f, hero_image_url: mediaRaw(m.id), thumbnail_url: mediaRaw(m.id) }));
    if (picker === "video") set("videos", [...(form.videos || []), { title: m.title, url: mediaRaw(m.id), media_id: m.id }]);
    if (picker === "pdf") set("pdfs", [...(form.pdfs || []), { title: m.title, media_id: m.id }]);
    setPicker(null);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto bg-[#0b1730] border-cyan-500/20 text-white" data-testid="solution-editor">
        <DialogHeader><DialogTitle className="font-head">{initial.id ? "Edit" : "New"} Solution</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Field label="Name"><Input data-testid="sol-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-[#050b18] border-slate-700" /></Field>
          <Field label="Subtitle"><Input value={form.subtitle} onChange={(e) => set("subtitle", e.target.value)} className="bg-[#050b18] border-slate-700" /></Field>
          <Field label="Short Description"><Input value={form.short_description} onChange={(e) => set("short_description", e.target.value)} className="bg-[#050b18] border-slate-700" /></Field>
          <Field label="Description">
            <textarea data-testid="sol-desc" value={form.description} onChange={(e) => set("description", e.target.value)} rows={4}
              className="w-full rounded-lg border border-slate-700 bg-[#050b18] px-3 py-2 text-sm outline-none focus:border-cyan-500" />
          </Field>
          <Field label="Icon">
            <select value={form.icon} onChange={(e) => set("icon", e.target.value)} data-testid="sol-icon"
              className="w-full rounded-lg border border-slate-700 bg-[#050b18] px-3 py-2 text-sm outline-none focus:border-cyan-500">
              {ICON_NAMES.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </Field>

          <Field label="Hero Image">
            <div className="flex items-center gap-3">
              {form.hero_image_url && <img src={form.hero_image_url} alt="" className="h-16 w-24 rounded-lg object-cover" />}
              <Button type="button" variant="outline" onClick={() => setPicker("hero")} data-testid="sol-pick-hero" className="border-slate-700">Choose image</Button>
              {form.hero_image_url && <button onClick={() => { set("hero_image_url", ""); set("thumbnail_url", ""); }} className="text-rose-300"><X className="h-4 w-4" /></button>}
            </div>
          </Field>

          <LinesEditor label="Key Benefits" value={form.benefits} onChange={(v) => set("benefits", v)} testId="sol-benefits" />
          <LinesEditor label="Capabilities" value={form.capabilities} onChange={(v) => set("capabilities", v)} testId="sol-capabilities" />
          <LinesEditor label="Asset Types" value={form.asset_types} onChange={(v) => set("asset_types", v)} testId="sol-assets" />
          <LinesEditor label="Industry Associations" value={form.industries} onChange={(v) => set("industries", v)} />

          <Field label="Videos">
            <div className="space-y-2">
              {(form.videos || []).map((v, i) => (
                <div key={i} className="flex items-center gap-2 rounded-lg bg-[#050b18] px-3 py-2 text-sm">
                  <Play className="h-4 w-4 text-cyan-300" /><span className="flex-1 truncate">{v.title}</span>
                  <button onClick={() => set("videos", form.videos.filter((_, j) => j !== i))} className="text-rose-300"><X className="h-4 w-4" /></button>
                </div>
              ))}
              <div className="flex flex-wrap gap-2">
                <Input placeholder="External video title" value={vTitle} onChange={(e) => setVTitle(e.target.value)} className="flex-1 bg-[#050b18] border-slate-700" />
                <Input placeholder="https://...mp4" value={vUrl} onChange={(e) => setVUrl(e.target.value)} className="flex-1 bg-[#050b18] border-slate-700" />
                <Button type="button" variant="outline" className="border-slate-700" onClick={() => { if (vUrl) { set("videos", [...(form.videos || []), { title: vTitle || "Video", url: vUrl }]); setVTitle(""); setVUrl(""); } }}>Add URL</Button>
                <Button type="button" variant="outline" className="border-slate-700" onClick={() => setPicker("video")} data-testid="sol-pick-video">From library</Button>
              </div>
            </div>
          </Field>

          <Field label="PDF Documents">
            <div className="space-y-2">
              {(form.pdfs || []).map((p, i) => (
                <div key={i} className="flex items-center gap-2 rounded-lg bg-[#050b18] px-3 py-2 text-sm">
                  <FileText className="h-4 w-4 text-cyan-300" /><span className="flex-1 truncate">{p.title}</span>
                  <button onClick={() => set("pdfs", form.pdfs.filter((_, j) => j !== i))} className="text-rose-300"><X className="h-4 w-4" /></button>
                </div>
              ))}
              <Button type="button" variant="outline" className="border-slate-700" onClick={() => setPicker("pdf")} data-testid="sol-pick-pdf">Add from library</Button>
            </div>
          </Field>

          <label className="flex items-center gap-3 rounded-lg bg-[#050b18] px-4 py-3">
            <input type="checkbox" checked={form.email_enabled} onChange={(e) => set("email_enabled", e.target.checked)} data-testid="sol-email-enabled" className="h-4 w-4 accent-cyan-500" />
            <span className="text-sm text-slate-300">Allow visitors to email documents</span>
          </label>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="outline" className="border-slate-700" disabled={saving} onClick={() => save(false)} data-testid="sol-save-draft">Save Draft</Button>
          <Button className="cyan-grad text-[#041021] font-bold" disabled={saving} onClick={() => save(true)} data-testid="sol-publish">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save & Publish"}
          </Button>
        </DialogFooter>

        {picker && <MediaPicker open onClose={() => setPicker(null)} kind={picker === "hero" ? "image" : picker} onSelect={onPick} />}
      </DialogContent>
    </Dialog>
  );
}
